/**
 * useDataGridEditing
 *
 * Manages per-cell editing state: when a cell is active and the user
 * starts typing, this hook tracks the editing value and handles
 * commit (save) and cancel (revert) flows.
 *
 * "Editing" means an input is focused inside the active cell.
 * This is distinct from "edit mode" which is the grid-level toggle.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { CellPosition, CellEdit } from './types';

export interface UseDataGridEditingOptions {
  activeCell: CellPosition | null;
  /** Callback to get row data by visual index from table row model */
  getRowData: (rowIndex: number) => Record<string, unknown> | null;
  doctype: string;
  saveCell: (edit: CellEdit) => Promise<boolean>;
}

export interface UseDataGridEditingReturn {
  isCellEditing: boolean;
  editingValue: unknown;
  startEditing: () => void;
  commitEdit: (value: unknown) => void;
  cancelEdit: () => void;
}

export function useDataGridEditing(
  opts: UseDataGridEditingOptions,
): UseDataGridEditingReturn {
  const [isCellEditing, setIsCellEditing] = useState(false);
  const [editingValue, setEditingValue] = useState<unknown>(null);
  const originalValueRef = useRef<unknown>(null);
  const committingRef = useRef(false);

  // Reset editing state when active cell changes
  useEffect(() => {
    setIsCellEditing(false);
    setEditingValue(null);
    originalValueRef.current = null;
    committingRef.current = false;
  }, [opts.activeCell?.rowIndex, opts.activeCell?.columnId]);

  const startEditing = useCallback(() => {
    if (!opts.activeCell) return;
    const row = opts.getRowData(opts.activeCell.rowIndex);
    if (!row) return;

    const currentValue = row[opts.activeCell.columnId];
    originalValueRef.current = currentValue;
    setEditingValue(currentValue);
    setIsCellEditing(true);
    committingRef.current = false;
  }, [opts.activeCell, opts.getRowData]);

  const commitEdit = useCallback(
    (value: unknown) => {
      // Idempotency guard: blur fires after Enter, skip if already committed
      if (committingRef.current || !isCellEditing) return;
      committingRef.current = true;
      setIsCellEditing(false);

      if (!opts.activeCell) return;

      const oldValue = originalValueRef.current;
      // Skip save if value unchanged
      if (value === oldValue) return;

      const row = opts.getRowData(opts.activeCell.rowIndex);
      if (!row) return;
      const name = row.name as string;
      if (!name) return;

      // Fire and forget — mutation hook handles optimistic update + rollback
      opts.saveCell({
        doctype: opts.doctype,
        name,
        fieldname: opts.activeCell.columnId,
        value,
        oldValue,
      });
    },
    [isCellEditing, opts],
  );

  const cancelEdit = useCallback(() => {
    setIsCellEditing(false);
    setEditingValue(originalValueRef.current);
  }, []);

  return {
    isCellEditing,
    editingValue,
    startEditing,
    commitEdit,
    cancelEdit,
  };
}
